import { Skeleton } from '@chakra-ui/react'
import styled from '@emotion/styled'
import React from 'react'
import { ConfirmModalParams, Items, ProductionOrder, Products, TmrItem, TmrProduct } from 'stylewhere/api'
import { Box, Button, InfoBox, Modal, ModalProps, ProductInfoBox } from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'
import { getEncodingProductFields } from 'stylewhere/shared/utils'

interface Props {
  title: string
  subtitle?: string
  isOpen?: boolean
  onConfirm?: () => void
  onClose: () => void
  style?: any
  labelClose?: string
  labelConfirm?: string
  /** if present show a product information box inside the confirmation modal */
  showDetailForProduct?: ConfirmModalParams['showDetailForProduct']
}

interface ProductDetails {
  product?: TmrProduct
  productionOrder?: ProductionOrder
}

export const ConfirmModal = (props: Props) => {
  const {
    isOpen,
    onClose,
    title,
    onConfirm,
    subtitle,
    labelClose,
    labelConfirm,
    showDetailForProduct: productToShow
  } = props
  const [detailProduct, setDetailProduct] = React.useState<ProductDetails | undefined>(undefined)
  const [productLoading, setProductLoading] = React.useState<boolean>(false)

  const btn1 = { flex: 1, minWidth: 'auto', height: 60 }
  const btn2 = { flex: 1, minWidth: 'auto', marginLeft: 15, height: 60 }

  React.useEffect(() => {
    async function fetchProductDetail() {
      if (productToShow && (productToShow.productCode || productToShow.identifierCode)) {
        try {
          setProductLoading(true)
          if (productToShow.productCode) {
            const products = await Products.searchCodes([productToShow.productCode])
            if (products.content.length) {
              setDetailProduct({ product: products.content[0] })
            }
          } else if (productToShow.identifierCode) {
            const items = await Items.search<TmrItem>({ identifierCodes: [productToShow.identifierCode] })
            if (items.content.length) {
              const item = items.content[0]
              const product = item.product
              if (product) {
                setDetailProduct({ product })
              }
              if (item.productionOrderRow?.productionOrder) {
                setDetailProduct((prev) => ({ ...prev, productionOrder: item.productionOrderRow.productionOrder }))
              }
            }
          }
        } catch (error) {
          console.error(error)
        } finally {
          setProductLoading(false)
        }
      }
    }
    fetchProductDetail()
  }, [productToShow])

  const size: ModalProps['size'] = productToShow ? '2xl' : 'xl'

  return (
    <Modal visible={isOpen ?? true} onClose={onClose} size={size} closeOnOverlayClick>
      <Box>
        <Title>{title}</Title>
        <Description>{subtitle}</Description>
      </Box>

      {productToShow && (
        <Skeleton loading={productLoading}>
          {detailProduct?.productionOrder && (
            <Box mb={25}>
              <InfoBox
                title={__(T.misc.production_order)}
                subtitle={detailProduct?.productionOrder?.code}
                data={detailProduct?.productionOrder}
                fields={EncodingExtensions.getProductionOrderFields()}
              />
            </Box>
          )}
          <Box mb={25}>
            {detailProduct?.product && (
              <ProductInfoBox
                product={detailProduct.product}
                paddingCard={'20px 20px 20px 20px'}
                fields={getEncodingProductFields()}
              />
            )}
          </Box>
        </Skeleton>
      )}

      <Box row mt={10}>
        <Button variant="default" title={labelClose || __(T.misc.cancel)} onClick={onClose} style={btn1} />
        <Button title={labelConfirm || __(T.misc.confirm)} onClick={onConfirm} style={btn2} />
      </Box>
    </Modal>
  )
}

const Title = styled.span`
  font-size: 26px;
  font-weight: 700;
  text-align: center;
  margin-top: 10px;
`

const Description = styled.label`
  font-weight: 500;
  padding: 25px 15px;
  font-size: 21px;
  white-space: pre-line;
  text-align: center;
  opacity: 0.5;
`
